import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import userAPI from '../../services/userAPI';
import { formatMoney } from '../../utils/formatMoney';
import { getAbsoluteImageUrl } from '../../utils/imageUrl';
import { ArrowLeft, Heart, MapPin, UtensilsCrossed, Star } from 'lucide-react';
import { getCurrencyFromPhone } from '../../utils/phone';
import './Profile.css';

const Favorites = () => {
    const [activeTab, setActiveTab] = useState('restaurants');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const getItemSymbol = (item) => {
        if (activeTab === 'products') {
            return getCurrencyFromPhone(item.restaurant?.contactNumber || item.restaurant?.phone);
        }
        return '';
    };

    useEffect(() => {
        fetchFavorites();
    }, [activeTab]);

    const fetchFavorites = async () => {
        try {
            setLoading(true);
            let data;
            if (activeTab === 'restaurants') {
                data = await userAPI.getFavoriteRestaurants();
            } else {
                data = await userAPI.getFavoriteProducts();
            }
            setItems(data.favorites || []);
        } catch (err) {
            console.error('Failed to fetch favorites:', err);
        } finally {
            setLoading(false);
        }
    };

    const getLangText = (data) => {
        if (!data) return "";
        if (typeof data === 'string') return data;
        return data.en || data.de || data.ar || (data && Object.values(data)[0]) || "";
    };

    return (
        <div className="profile-dashboard-page">
            <div className="premium-profile-header-container" style={{ paddingBottom: '60px' }}>
                <button onClick={() => navigate(-1)} className="header-back-btn">
                    <ArrowLeft size={20} />
                </button>
                <div className="header-profile-info">
                    <div className="icon-circle" style={{ background: 'white', color: '#C5192D', width: '60px', height: '60px', marginBottom: '12px' }}>
                        <Heart size={32} fill="#C5192D" />
                    </div>
                    <h2 className="dashboard-user-name">My Favorites</h2>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0 0', fontSize: '14px' }}>
                        Your most loved restaurants and dishes
                    </p>
                </div>
            </div>

            <div className="dashboard-content" style={{ marginTop: '-30px' }}>
                <div className="dashboard-top-actions" style={{ justifyContent: 'center', background: 'white', padding: '8px', borderRadius: '30px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
                    <button 
                        onClick={() => setActiveTab('restaurants')}
                        className={`tab-pill ${activeTab === 'restaurants' ? 'active' : ''}`}
                        style={{
                            flex: 1,
                            padding: '12px 20px',
                            borderRadius: '25px',
                            border: 'none',
                            background: activeTab === 'restaurants' ? '#C5192D' : 'transparent',
                            color: activeTab === 'restaurants' ? 'white' : '#64748b',
                            fontWeight: '700',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                    >
                        Restaurants
                    </button>
                    <button 
                        onClick={() => setActiveTab('products')}
                        className={`tab-pill ${activeTab === 'products' ? 'active' : ''}`}
                        style={{
                            flex: 1,
                            padding: '12px 20px',
                            borderRadius: '25px',
                            border: 'none',
                            background: activeTab === 'products' ? '#C5192D' : 'transparent',
                            color: activeTab === 'products' ? 'white' : '#64748b',
                            fontWeight: '700',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                        }}
                    >
                        Dishes
                    </button>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 animate-pulse">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} style={{ height: 220, background: '#f1f5f9', borderRadius: 24 }}></div>
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '80px 20px', background: 'white', borderRadius: '32px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
                        <div style={{ width: '80px', height: '80px', background: '#f8fafc', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyCenter: 'center', margin: '0 auto 20px', color: '#cbd5e1' }}>
                            {activeTab === 'restaurants' ? <MapPin size={40} /> : <UtensilsCrossed size={40} />}
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#1e293b', margin: '0 0 8px' }}>No Favorites Yet</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '250px', margin: '0 auto 24px' }}>
                            Start adding items to your favorites to see them here!
                        </p>
                        <button 
                            onClick={() => navigate('/home')}
                            style={{ background: '#1e293b', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '14px', fontWeight: '700', cursor: 'pointer' }}
                        >
                            Explore Now
                        </button>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                        gap: '20px'
                    }}>
                        {items.map((item) => (
                            <div 
                                key={item._id} 
                                onClick={() => navigate(activeTab === 'restaurants' ? `/restaurant/${item._id}` : `/restaurant/${item.restaurant?._id || item.restaurant}`)}
                                style={{ 
                                    background: 'white', 
                                    borderRadius: '24px', 
                                    padding: '12px',
                                    boxShadow: '0 10px 25px rgba(0,0,0,0.04)',
                                    cursor: 'pointer',
                                    transition: 'transform 0.3s',
                                    border: '1px solid #f1f5f9'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={{
                                    width: '100%',
                                    height: '140px',
                                    backgroundImage: getAbsoluteImageUrl(item.image || item.images?.[0]) ? `url(${getAbsoluteImageUrl(item.image || item.images?.[0])})` : 'none',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: '18px',
                                    marginBottom: '12px',
                                    position: 'relative'
                                }}>
                                    <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'white', padding: '4px 8px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '800', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                        <Star size={12} fill="#FFB800" color="#FFB800" />
                                        <span>
                                            {activeTab === 'restaurants' 
                                                ? (item.rating?.average?.toFixed(1) || '0.0') 
                                                : (item.restaurant?.rating?.average?.toFixed(1) || '4.0')}
                                        </span>
                                    </div>
                                </div>
                                <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '800', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {getLangText(item.name)}
                                </h3>
                                {activeTab === 'products' ? (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '14px', fontWeight: '800', color: '#C5192D' }}>
                                            {formatMoney(item.basePrice, { symbol: getItemSymbol(item) })}
                                        </span>
                                        <div style={{ width: '28px', height: '28px', background: '#fef2f2', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C5192D' }}>
                                            <UtensilsCrossed size={14} />
                                        </div>
                                    </div>
                                ) : (
                                    <p style={{ margin: 0, fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <MapPin size={12} /> {item.city || 'Kuwait City'}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Favorites;
